import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, LoadingSpinner } from '../../components/atoms';
import { FormField } from '../../components/molecues/FormField';
import { DurationHmsField } from '../../components/molecues/DurationHmsField';
import { useSeriesActions } from '../../hooks/useSeries';
import { useCastActions, useCastAssignment } from '../../hooks/useCasts';
import { getLanguageService, getGenreService } from '../../service/ServiceRegistry';
import type { Language } from '../../types/api/language';
import type { Genre } from '../../types/api/genre';
import type { DramaDetailResponse, UpdateDramaRequest } from '../../types/api/drama';
import type { Episode } from '../../types/api/episode';
import { useEpisodes, useEpisodeActions } from '../../hooks/useEpisodes';
import type { Column, TableAction } from '../../types/table';
import { DataTable, PreviewImagesSection } from '../../components/organisms';
import { DeleteConfirmationModal } from '../../components/organisms/DeleteConfirmationModal';
import { EpisodeFormModal, type EpisodeFormValues } from '../../components/organisms/EpisodeFormModal';
import { EpisodeVideoUploadModal } from '../../components/organisms/EpisodeVideoUploadModal';
import { formatSeasonEpisodeLabel } from '../../types/api/episode';
import { formatDateForDisplay } from '../../types/api/drama';
import { resolveAssetUrl } from '../../utils/url';
import toast from 'react-hot-toast';
import { CastAssignmentModal } from '../../components/organisms/CastAssignmentModal';
import type {
  PreviewImageMetadataRequest,
  PreviewImageResponse,
} from '../../types/api/movie';

/**
 * SeriesEditPage Component
 *
 * Separate page for editing series metadata.
 * Features:
 * - Load existing series data (via DramaDetailResponse)
 * - Edit core fields (title, description, language)
 * - Edit seasons/episodes/duration, access level and status
 * - Manage genres and primary genre
 * - View current thumbnail
 */
export const SeriesEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { seriesId } = useParams<{ seriesId: string }>();
  const {
    getSeries,
    updateSeries,
    uploadPreviewImages,
    updatePreviewImageMetadata,
    updatePreviewImageWithFile,
  } = useSeriesActions();
  const languageService = getLanguageService();
  const genreService = getGenreService();

  const [seriesDetails, setSeriesDetails] = useState<DramaDetailResponse | null>(null);
  const [formData, setFormData] = useState<UpdateDramaRequest>({
    title: '',
    description: '',
    languageId: '',
    totalSeasons: 1,
    totalEpisodes: 1,
    episodeDuration: 2700,
    priority: undefined,
    accessLevel: 'FREE',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loadError, setLoadError] = useState<string | null>(null);

  // Options for dropdowns
  const [languages, setLanguages] = useState<Language[]>([]);
  const [genres, setGenres] = useState<Genre[]>([]);
  const [resolvedDramaId, setResolvedDramaId] = useState<string>('');

  // Episodes state
  const [seasons, setSeasons] = useState<number[]>([]);
  const [episodesRefreshKey, setEpisodesRefreshKey] = useState(0);
  const {
    fetchEpisodes,
    seasonFilter,
    setSeasonFilter,
  } = useEpisodes(resolvedDramaId);
  const {
    createEpisodeWithJson,
    createEpisodeWithThumbnail,
    updateEpisode,
    permanentDeleteEpisode,
    getSeasons,
  } = useEpisodeActions(resolvedDramaId);
  const [isEpisodeModalOpen, setIsEpisodeModalOpen] = useState(false);
  const [episodeModalMode, setEpisodeModalMode] = useState<'create' | 'edit'>('create');
  const [selectedEpisode, setSelectedEpisode] = useState<Episode | null>(null);
  const [isEpisodeDeleting, setIsEpisodeDeleting] = useState(false);
  const [isEpisodeDeleteModalOpen, setIsEpisodeDeleteModalOpen] = useState(false);
  const [isEpisodeVideoModalOpen, setIsEpisodeVideoModalOpen] = useState(false);
  const [selectedEpisodeForVideo, setSelectedEpisodeForVideo] = useState<Episode | null>(null);

  const [isCastModalOpen, setIsCastModalOpen] = useState(false);
  const { searchCasts } = useCastActions();
  const contentIdForCasts = resolvedDramaId || seriesId || '';
  const {
    assignCast,
    removeCast,
    updateRole,
    getCasts,
  } = useCastAssignment('drama', contentIdForCasts);

  const [isPreviewImagesBusy, setIsPreviewImagesBusy] = useState(false);

  // Load series data
  useEffect(() => {
    if (!seriesId) {
      toast.error('Series ID is required');
      navigate('/pages/series');
      return;
    }

    const loadSeries = async () => {
      try {
        setIsLoading(true);
        setLoadError(null);

        const [seriesDetailsData, langsResponse, genresResponse] = await Promise.all([
          getSeries(seriesId),
          languageService.getLanguages({ page: 1, size: 100 }),
          genreService.getPublicGenres({ page: 1, size: 200 }),
        ]);

        setSeriesDetails(seriesDetailsData);
        setLoadError(null);

        const drama = seriesDetailsData.drama;
        const effectiveDramaId = drama.dramaId ?? '';
        setResolvedDramaId(effectiveDramaId);
        const genreIds = seriesDetailsData.genres.map((g) => g.genreId);
        const primaryGenreId = seriesDetailsData.primaryGenre?.genreId || '';

        setFormData({
          title: drama.title,
          description: drama.description,
          languageId: drama.languageId,
          totalSeasons: drama.totalSeasons,
          totalEpisodes: drama.totalEpisodes,
          episodeDuration: drama.episodeDuration,
          priority: drama.priority ?? undefined,
          accessLevel: drama.accessLevel,
          initialGenreIds: genreIds,
          primaryGenreId,
          thumbnailUrl: drama.thumbnailUrl || undefined,
        });
        setLanguages(langsResponse.items);
        setGenres(genresResponse.items);
      } catch (error: any) {
        console.error('Failed to load series:', error);
        setLoadError(error.message || 'Failed to load series');
      } finally {
        setIsLoading(false);
      }
    };

    loadSeries();
  }, [seriesId, navigate, getSeries, languageService, genreService]);

  useEffect(() => {
    if (!resolvedDramaId) {
      setSeasons([]);
      return;
    }

    const loadSeasons = async () => {
      try {
        const seasonsResponse = await getSeasons();
        setSeasons(seasonsResponse);
      } catch (error) {
        console.error('Failed to load seasons:', error);
        setSeasons([]);
      }
    };

    loadSeasons();
  }, [resolvedDramaId, getSeasons]);

  const handleChange = (field: keyof UpdateDramaRequest, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleGenreToggle = (genreId: string) => {
    setFormData((prev) => {
      const currentIds = prev.initialGenreIds || [];
      const exists = currentIds.includes(genreId);
      const updatedIds = exists
        ? currentIds.filter((id) => id !== genreId)
        : [...currentIds, genreId];

      const updatedPrimary =
        exists && prev.primaryGenreId === genreId ? '' : prev.primaryGenreId;

      return {
        ...prev,
        initialGenreIds: updatedIds,
        primaryGenreId: updatedPrimary,
      };
    });
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.initialGenreIds;
      delete newErrors.primaryGenreId;
      return newErrors;
    });
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title?.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!formData.description?.trim()) {
      newErrors.description = 'Description is required';
    }
    if (!formData.languageId) {
      newErrors.languageId = 'Language is required';
    }
    if (!formData.totalSeasons || formData.totalSeasons < 1) {
      newErrors.totalSeasons = 'Total seasons must be at least 1';
    }
    if (!formData.totalEpisodes || formData.totalEpisodes < 1) {
      newErrors.totalEpisodes = 'Total episodes must be at least 1';
    }
    if (!formData.episodeDuration || formData.episodeDuration < 1) {
      newErrors.episodeDuration = 'Episode duration must be at least 1 second';
    }
    if (!formData.initialGenreIds || formData.initialGenreIds.length === 0) {
      newErrors.initialGenreIds = 'At least one genre is required';
    }
    if (!formData.primaryGenreId) {
      newErrors.primaryGenreId = 'Primary genre is required';
    }

    if (
      formData.priority !== undefined &&
      (!Number.isInteger(formData.priority) || formData.priority < 0)
    ) {
      newErrors.priority = 'Priority must be an integer >= 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!seriesId || !validate()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await updateSeries(seriesId, formData);
      toast.success('Series updated successfully');
      navigate('/pages/series');
    } catch (error: any) {
      toast.error(error.message || 'Failed to update series');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEpisodesRefresh = useCallback(() => {
    setEpisodesRefreshKey((prev) => prev + 1);
  }, []);

  const handleOpenCreateEpisode = () => {
    setSelectedEpisode(null);
    setEpisodeModalMode('create');
    setIsEpisodeModalOpen(true);
  };

  const handleOpenEditEpisode = (episode: Episode) => {
    setSelectedEpisode(episode);
    setEpisodeModalMode('edit');
    setIsEpisodeModalOpen(true);
  };

  const handleEpisodeSubmit = async (values: EpisodeFormValues) => {
    if (!resolvedDramaId) return;

    try {
      if (episodeModalMode === 'create') {
        const payload = {
          seasonNumber: values.seasonNumber,
          episodeNumber: values.episodeNumber,
          title: values.title,
          description: values.description,
          releaseDate: values.releaseDate,
          duration: values.duration,
          thumbnailUrl: values.thumbnailUrl || undefined,
        };

        if (values.thumbnailFile) {
          await createEpisodeWithThumbnail(payload, values.thumbnailFile);
        } else {
          await createEpisodeWithJson(payload);
        }
        toast.success('Episode created successfully');
      } else if (selectedEpisode) {
        await updateEpisode(selectedEpisode.episodeId, {
          seasonNumber: values.seasonNumber,
          episodeNumber: values.episodeNumber,
          title: values.title,
          description: values.description,
          releaseDate: values.releaseDate,
          duration: values.duration,
          thumbnailUrl: values.thumbnailUrl || null,
        });
        toast.success('Episode updated successfully');
      }
      setIsEpisodeModalOpen(false);
      setSelectedEpisode(null);
      handleEpisodesRefresh();
    } catch (error: any) {
      toast.error(error.message || 'Failed to save episode');
    }
  };

  const handleOpenDeleteEpisode = (episode: Episode) => {
    setSelectedEpisode(episode);
    setIsEpisodeDeleteModalOpen(true);
  };

  const handleOpenEpisodeVideoModal = (episode: Episode) => {
    setSelectedEpisodeForVideo(episode);
    setIsEpisodeVideoModalOpen(true);
  };

  const handleConfirmDeleteEpisode = async () => {
    if (!selectedEpisode) return;

    setIsEpisodeDeleting(true);
    try {
      await permanentDeleteEpisode(selectedEpisode.episodeId);
      toast.success('Episode permanently deleted successfully');
      setIsEpisodeDeleteModalOpen(false);
      setSelectedEpisode(null);
      handleEpisodesRefresh();
    } catch (error: any) {
      toast.error(error.message || 'Failed to permanently delete episode');
    } finally {
      setIsEpisodeDeleting(false);
    }
  };

  const episodeColumns = useMemo<Column<Episode>[]>(
    () => [
      {
        id: 'episode',
        header: 'Episode',
        accessor: 'title',
        cell: (row) => (
          <div className="flex flex-col text-left">
            <span className="text-xs font-semibold text-gray-500">
              {formatSeasonEpisodeLabel(row.seasonNumber, row.episodeNumber)}
            </span>
            <span className="text-sm font-semibold text-gray-900 truncate">
              {row.title}
            </span>
            <span className="text-xs text-gray-500">
              {formatDateForDisplay(row.releaseDate)}
            </span>
          </div>
        ),
        minWidth: '220px',
      },
      {
        id: 'description',
        header: 'Description',
        accessor: 'description',
        cell: (row) => (
          <p className="text-sm text-gray-700 line-clamp-2 max-w-xs text-left">
            {row.description}
          </p>
        ),
        hideOnMobile: true,
      },
      {
        id: 'duration',
        header: 'Duration',
        accessor: 'duration',
        cell: (row) => (
          <span className="text-sm text-gray-700 text-left">
            {row.duration} min
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        accessor: 'status',
        cell: (row) => {
          const statusColors: Record<string, string> = {
            ACTIVE: 'bg-green-100 text-green-800',
            INACTIVE: 'bg-gray-100 text-gray-800',
            PENDING: 'bg-yellow-100 text-yellow-800',
          };

          return (
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                statusColors[row.status] || 'bg-gray-100 text-gray-800'
              }`}
            >
              {row.status}
            </span>
          );
        },
        hideOnMobile: true,
      },
      {
        id: 'uploadDate',
        header: 'Uploaded',
        accessor: 'uploadDate',
        cell: (row) => (
          <span className="text-sm text-gray-600 text-left">
            {formatDateForDisplay(row.uploadDate)}
          </span>
        ),
        hideOnMobile: true,
      },
    ],
    []
  );

  const episodeActionsConfig = useMemo<TableAction<Episode>[]>(
    () => [
      {
        id: 'edit',
        label: 'Edit',
        variant: 'ghost',
        icon: (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
          </svg>
        ),
        onClick: handleOpenEditEpisode,
      },
      {
        id: 'videos',
        label: 'Manage Videos',
        variant: 'ghost',
        icon: (
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 6h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z"
            />
          </svg>
        ),
        onClick: handleOpenEpisodeVideoModal,
      },
      {
        id: 'delete',
        label: 'Delete',
        variant: 'danger',
        icon: (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        ),
        onClick: handleOpenDeleteEpisode,
      },
    ],
    [handleOpenEditEpisode]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!seriesDetails || !seriesDetails.drama) {
    return (
      <div className="text-center py-12">
        {loadError ? (
          <>
            <p className="text-red-600 mb-4">{loadError}</p>
            <div className="flex items-center justify-center gap-3">
              <Button
                variant="primary"
                onClick={() => window.location.reload()}
                className="mt-4"
              >
                Retry
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/pages/series')}
                className="mt-4"
              >
                Back to Series
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-gray-600">Series not found</p>
            <Button
              variant="outline"
              onClick={() => navigate('/pages/series')}
              className="mt-4"
            >
              Back to Series
            </Button>
          </>
        )}
      </div>
    );
  }

  const drama = seriesDetails.drama;

  const handleFetchAssignedCasts = async () => {
    return await getCasts();
  };

  const handleSearchCasts = async (keyword: string) => {
    const page = 0;
    const size = 10;
    const pageData = await searchCasts(keyword, page, size);
    return pageData.items;
  };

  const handleUploadPreviewImages = async (files: File[]) => {
    const dramaIdForPreview = resolvedDramaId || seriesId;

    if (!dramaIdForPreview) {
      toast.error('Series identifier is missing');
      return;
    }

    const existingCount = (seriesDetails.previewImages || []).length;
    const maxTotal = 10;
    const maxPerUpload = 5;

    if (files.length === 0) {
      toast.error('Please select at least one image to upload');
      return;
    }

    if (files.length > maxPerUpload) {
      toast.error(`You can upload up to ${maxPerUpload} images per request`);
      return;
    }

    if (existingCount >= maxTotal) {
      toast.error('Maximum 10 preview images per series reached');
      return;
    }

    if (existingCount + files.length > maxTotal) {
      toast.error(
        `You can upload only ${maxTotal - existingCount} more image${
          maxTotal - existingCount > 1 ? 's' : ''
        } for this series`
      );
      return;
    }

    setIsPreviewImagesBusy(true);
    try {
      await uploadPreviewImages(dramaIdForPreview, files);
      const updatedDetails = await getSeries(seriesId!);
      setSeriesDetails(updatedDetails);
      toast.success('Preview images uploaded successfully');
    } catch (error: any) {
      console.error('Failed to upload preview images for series:', error);
      toast.error(error?.message || 'Failed to upload preview images');
    } finally {
      setIsPreviewImagesBusy(false);
    }
  };

  const handleUpdatePreviewImageMetadata = async (
    previewImageId: string,
    metadata: PreviewImageMetadataRequest
  ) => {
    const dramaIdForPreview = resolvedDramaId || seriesId;

    if (!dramaIdForPreview) {
      toast.error('Series identifier is missing');
      return;
    }

    setIsPreviewImagesBusy(true);
    try {
      await updatePreviewImageMetadata(dramaIdForPreview, previewImageId, metadata);
      const updatedDetails = await getSeries(seriesId!);
      setSeriesDetails(updatedDetails);
      toast.success('Preview image updated successfully');
    } catch (error: any) {
      console.error('Failed to update preview image metadata for series:', error);
      toast.error(error?.message || 'Failed to update preview image');
    } finally {
      setIsPreviewImagesBusy(false);
    }
  };

  const handleReplacePreviewImage = async (
    previewImageId: string,
    metadata: PreviewImageMetadataRequest,
    file?: File
  ) => {
    const dramaIdForPreview = resolvedDramaId || seriesId;

    if (!dramaIdForPreview) {
      toast.error('Series identifier is missing');
      return;
    }

    setIsPreviewImagesBusy(true);
    try {
      await updatePreviewImageWithFile(dramaIdForPreview, previewImageId, metadata, file);
      const updatedDetails = await getSeries(seriesId!);
      setSeriesDetails(updatedDetails);
      toast.success('Preview image updated successfully');
    } catch (error: any) {
      console.error('Failed to replace preview image for series:', error);
      toast.error(error?.message || 'Failed to update preview image');
    } finally {
      setIsPreviewImagesBusy(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="text-left">
          <h1 className="text-lg font-bold text-gray-900">Edit Series</h1>
          <p className="text-gray-600 mt-1">Update series information</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            onClick={() => setIsCastModalOpen(true)}
          >
            Manage Casts
          </Button>
          <Button variant="outline" onClick={() => navigate('/pages/series')}>
            Cancel
          </Button>
        </div>
      </div>

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6"
      >
        {/* Basic Information */}
        <div className="space-y-4">
          <h2 className="text-base text-left font-semibold text-gray-900 pb-2">
            Basic Information
          </h2>

          <FormField
            label="Title"
            value={formData.title || ''}
            onChange={(e) => handleChange('title', e.target.value)}
            error={errors.title}
            required
            placeholder="Enter series title"
          />

          <div className="w-full">
            <label className="block text-sm font-medium text-left text-gray-700 mb-1.5">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Enter series description"
              rows={4}
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
            />
            {errors.description && (
              <p className="text-sm text-red-600 mt-1.5">{errors.description}</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="w-full">
              <label className="block text-sm font-medium text-left text-gray-700 mb-1.5">
                Language <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.languageId || ''}
                onChange={(e) => handleChange('languageId', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
              >
                <option value="">Select a language</option>
                {languages
                  .filter((lang) => lang.isActive)
                  .map((lang) => (
                    <option key={lang.languageId} value={lang.languageId}>
                      {lang.name} ({lang.nameMm})
                    </option>
                  ))}
              </select>
              {errors.languageId && (
                <p className="text-sm text-red-600 mt-1.5">{errors.languageId}</p>
              )}
            </div>

            <DurationHmsField
              label="Episode duration"
              valueSeconds={formData.episodeDuration}
              onSecondsChange={(sec) => handleChange('episodeDuration', sec ?? 0)}
              required
              error={errors.episodeDuration}
              helperText="Stored and sent as total seconds per episode."
              idPrefix="series-edit-episode-duration"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <FormField
              label="Total Seasons"
              type="number"
              value={formData.totalSeasons?.toString() || ''}
              onChange={(e) =>
                handleChange('totalSeasons', parseInt(e.target.value) || 0)
              }
              error={errors.totalSeasons}
              required
              min={1}
              placeholder="1"
            />

            <FormField
              label="Total Episodes"
              type="number"
              value={formData.totalEpisodes?.toString() || ''}
              onChange={(e) =>
                handleChange('totalEpisodes', parseInt(e.target.value) || 0)
              }
              error={errors.totalEpisodes}
              required
              min={1}
              placeholder="16"
            />

            <FormField
              label="Priority"
              type="number"
              value={formData.priority != null ? formData.priority.toString() : ''}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') {
                  handleChange('priority', undefined);
                  return;
                }
                const parsed = parseInt(raw, 10);
                handleChange('priority', Number.isNaN(parsed) ? undefined : parsed);
              }}
              error={errors.priority}
              min={0}
              placeholder="0"
            />

            <div className="w-full">
              <label className="block text-sm font-medium text-left text-gray-700 mb-1.5">
                Access Level
              </label>
              <select
                value={formData.accessLevel || 'FREE'}
                onChange={(e) => handleChange('accessLevel', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
              >
                <option value="FREE">Free</option>
                <option value="PREMIUM">Premium</option>
              </select>
            </div>
          </div>
        </div>

        {/* Genres */}
        <div className="space-y-4">
          <h2 className="text-base text-left pt-4 font-semibold text-gray-900 border-t border-dashed border-gray-600 pb-2">
            Genres
          </h2>

          <div className="w-full">
            <label className="block text-sm font-medium text-left text-gray-700 mb-1.5">
              Select Genres <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-4">
              {genres
                .filter((genre) => genre.isActive)
                .map((genre) => (
                  <label
                    key={genre.genreId}
                    className="flex items-center gap-2 cursor-pointer p-2 hover:bg-gray-50 rounded"
                  >
                    <input
                      type="checkbox"
                      checked={formData.initialGenreIds?.includes(genre.genreId) || false}
                      onChange={() => handleGenreToggle(genre.genreId)}
                      className="w-4 h-4 text-[#EAB308] border-gray-300 rounded focus:ring-[#EAB308]"
                    />
                    <span className="text-sm text-gray-700">{genre.name}</span>
                  </label>
                ))}
            </div>
            {errors.initialGenreIds && (
              <p className="text-sm text-red-600 mt-1.5">{errors.initialGenreIds}</p>
            )}
          </div>

          <div className="w-full">
            <label className="block text-sm font-medium text-left text-gray-700 mb-1.5">
              Primary Genre <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.primaryGenreId || ''}
              onChange={(e) => handleChange('primaryGenreId', e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
            >
              <option value="">Select primary genre</option>
              {genres
                .filter(
                  (genre) =>
                    genre.isActive && formData.initialGenreIds?.includes(genre.genreId)
                )
                .map((genre) => (
                  <option key={genre.genreId} value={genre.genreId}>
                    {genre.name}
                  </option>
                ))}
            </select>
            {errors.primaryGenreId && (
              <p className="text-sm text-red-600 mt-1.5">{errors.primaryGenreId}</p>
            )}
            {formData.initialGenreIds && formData.initialGenreIds.length > 0 && (
              <p className="text-sm text-gray-500 mt-1.5 text-left">
                Primary genre must be selected from the genres above.
              </p>
            )}
          </div>
        </div>

        {/* Episodes */}
        {resolvedDramaId ? (
          <div className="space-y-4">
            <h2 className="text-base text-left pt-4 font-semibold text-gray-900 border-t border-dashed border-gray-600 pb-2">
              Episodes
            </h2>

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="flex items-center gap-3">
                <label className="block text-sm font-medium text-left text-gray-700">
                  Season
                </label>
                <select
                  value={seasonFilter ?? ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSeasonFilter(value ? parseInt(value, 10) : null);
                    handleEpisodesRefresh();
                  }}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 focus:outline-none"
                >
                  <option value="">All seasons</option>
                  {seasons.map((season) => (
                    <option key={season} value={season}>
                      Season {season}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center justify-end">
                <Button variant="primary" size="sm" onClick={handleOpenCreateEpisode}>
                  Add Episode
                </Button>
              </div>
            </div>

            <DataTable<Episode>
              columns={episodeColumns}
              fetchData={fetchEpisodes}
              searchable
              searchPlaceholder="Search episodes by title or keyword..."
              initialPageSize={10}
              pageSizeOptions={[10, 20, 50]}
              actions={episodeActionsConfig}
              hoverable
              showRefreshButton
              onRefresh={handleEpisodesRefresh}
              caption="Episodes for this series"
              key={episodesRefreshKey}
            />
          </div>
        ) : (
          <div className="space-y-4">
            <h2 className="text-base text-left pt-4 font-semibold text-gray-900 border-t border-dashed border-gray-600 pb-2">
              Episodes
            </h2>
            <p className="text-sm text-gray-600">
              Episodes will be available once the series identifier is ready. Please refresh the page if this message
              persists.
            </p>
          </div>
        )}

        {/* Current Thumbnail */}
        {drama.thumbnailUrl && (
          <div className="space-y-4">
            <h2 className="text-base font-semibold text-gray-900 pb-2 text-left">
              Current Thumbnail
            </h2>
            <div className="relative">
              <img
                src={resolveAssetUrl(drama.thumbnailUrl)}
                alt={drama.title}
                className="w-48 h-64 object-cover rounded-lg border border-gray-300"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const fallback = target.nextElementSibling as HTMLElement;
                  if (fallback) fallback.style.display = 'flex';
                }}
              />
              <div
                className="w-48 h-64 bg-gray-200 rounded-lg border border-gray-300 flex items-center justify-center"
                style={{ display: 'none' }}
              >
                <div className="text-center">
                  <svg
                    className="w-12 h-12 text-gray-400 mx-auto mb-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <p className="text-xs text-gray-500">No Image</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Preview Images */}
        <PreviewImagesSection
          title="Preview Images"
          images={(seriesDetails.previewImages as PreviewImageResponse[]) || []}
          maxPerUpload={5}
          maxTotal={10}
          isBusy={isPreviewImagesBusy}
          onUpload={handleUploadPreviewImages}
          onUpdateMetadata={handleUpdatePreviewImageMetadata}
          onReplaceImage={handleReplacePreviewImage}
        />

        {/* Form Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/pages/series')}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" variant="primary" isLoading={isSubmitting}>
            Update Series
          </Button>
        </div>
      </form>

      {/* Episode Create/Edit Modal */}
      <EpisodeFormModal
        isOpen={isEpisodeModalOpen}
        onClose={() => {
          setIsEpisodeModalOpen(false);
          setSelectedEpisode(null);
        }}
        onSubmit={handleEpisodeSubmit}
        mode={episodeModalMode}
        episode={selectedEpisode || undefined}
        defaultSeason={
          selectedEpisode?.seasonNumber ?? seasonFilter ?? (seasons[0] ?? drama.totalSeasons)
        }
        defaultDuration={drama.episodeDuration}
      />

      {/* Episode Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isEpisodeDeleteModalOpen}
        onClose={() => {
          setIsEpisodeDeleteModalOpen(false);
          setSelectedEpisode(null);
        }}
        onConfirm={handleConfirmDeleteEpisode}
        title="Permanently Delete Episode"
        message={
          selectedEpisode
            ? `Are you sure you want to permanently delete ${formatSeasonEpisodeLabel(
                selectedEpisode.seasonNumber,
                selectedEpisode.episodeNumber
              )} - "${selectedEpisode.title}"? This will permanently remove the episode, all its videos, preview images, and associated data from the system. This action cannot be undone.`
            : 'Are you sure you want to permanently delete this episode? This will permanently remove the episode, all its videos, preview images, and associated data from the system. This action cannot be undone.'
        }
        isDeleting={isEpisodeDeleting}
      />

      {/* Episode Video Upload Modal */}
      {resolvedDramaId && selectedEpisodeForVideo && (
        <EpisodeVideoUploadModal
          isOpen={isEpisodeVideoModalOpen}
          onClose={() => {
            setIsEpisodeVideoModalOpen(false);
            setSelectedEpisodeForVideo(null);
          }}
          dramaId={resolvedDramaId}
          episodeId={selectedEpisodeForVideo.episodeId}
          episodeTitle={`${formatSeasonEpisodeLabel(
            selectedEpisodeForVideo.seasonNumber,
            selectedEpisodeForVideo.episodeNumber
          )} - ${selectedEpisodeForVideo.title}`}
        />
      )}

      {/* Cast Assignment Modal */}
      {resolvedDramaId && (
        <CastAssignmentModal
          isOpen={isCastModalOpen}
          onClose={() => setIsCastModalOpen(false)}
          contentType="drama"
          contentId={resolvedDramaId}
          contentTitle={drama.title}
          fetchAssignedCasts={handleFetchAssignedCasts}
          searchCasts={handleSearchCasts}
          onAssign={async (castId, role) => {
            await assignCast(castId, role);
          }}
          onRemove={async (castId) => {
            await removeCast(castId);
          }}
          onUpdateRole={async (castId, role) => {
            await updateRole(castId, role);
          }}
        />
      )}
    </div>
  );
};

export default SeriesEditPage;


